import { IPartnerOffice } from './../partner/index.js';
import { WorkflowAccessLevel } from '../apply/index.js';

export interface IApplyStudentNumberTracker {
  p_key?: string;
  s_key?: string;
  lastAssignedStudentNumber: string;
  lastOperatedUserID: string;
  lastUpdated: number;
}

export enum Platform {
  Apply = 'Apply',
  CasShield = 'CasShield',
}

export enum IUserRole {
  Admin = 'Admin',
  Enroly = 'Enroly',
  UniAdmin = 'UniAdmin',
  Uni = 'Uni',
  UniSecure = 'UniSecure',
  UniLite = 'UniLite',
  Agent = 'Agent',
  AgencyAdmin = 'AgencyAdmin',
  // Partner = Service Provider
  Partner = 'Partner',
  ImmigrationProvider = 'ImmigrationProvider',
  Student = 'Student',
  Applicant = 'Applicant',
  TempApplicant = 'TempApplicant',
}

export enum UserStatus {
  // User was created
  NEW = 'NEW',
  // User has been confirmed
  CONFIRMED = 'CONFIRMED',
  // User password was reset
  PASSWORD_RESET = 'PASSWORD_RESET',
}

export enum PathwayAccess {
  Full = 'Full',
  Partial = 'Partial',
  None = 'None',
}

export enum MfaMethod {
  TOTP = 'totp',
  SMS = 'sms',
  EMAIL = 'email',
}

export enum UserErrorMessages {
  USER_WITH_EMAIL_ALREADY_EXISTS = 'User with this email already exists',
  FAILED_TO_REGISTER_NEW_USER = 'Failed to register new user',
  FAILED_TO_LOCATE_USERPOOL = 'Failed to locate userpool',
  FAILED_TO_CREATE_APPLICANT_DETAILS = 'Failed to create applicant details',
  FAILED_TO_CREATE_APPLICATION = 'Failed to create application',
}

export interface IUser {
  p_key?: string;
  s_key?: string /* s_key is username */;
  userId?: string; // cognito.sub - used for applicants
  status: UserStatus;
  givenName: string;
  familyName: string;
  email: string;
  emailVerified?: boolean;
  userRole?: IUserRole | string /* empty userRole = user is a student */;
  intakeId?: string;
  studentNo?: string;
  terms?: boolean;
  marketing?: boolean;
  university?: string;
  agency?: string;
  agencyOffice?: string;
  regionAccessId?: string;
  OTP?: string;
  pathway?: string;
  pathwayAccess?: PathwayAccess | string;
  partner?: string;
  partnerOffice?: string;
  partnerOfficeDetails?: IPartnerOffice /* not stored in DB, frontend-only field */;
  immigrationProvider?: string;
  agencyName?: string /* not stored in DB, frontend-only field */;
  agencyOfficeName?: string /* not stored in DB, frontend-only field */;
  streakBoxUni?: string;
  streakBoxAgent?: string;
  pathwayMigrationPending?: boolean;
  mfaEnabled?: boolean;
  mfaSkippedDates?: string[]; // YYYY-MM-DD dates when user skipped MFA setup (max 3)
  phoneNumber?: string;
  termsAcceptedDate?: number; // unix timestamp of when the user accepted T&Cs
  beta?: boolean;
  insightsAccess?: boolean;
  refreshToken?: string;
  verifiedApplicant?: boolean;
  hasLoggedIn?: boolean; // used for applicant login flow: three state flag: undefined(initially), false (after user management login for auth tokens), true (on client auth)
  workflowAccess?: WorkflowAccessLevel;
  disableEmailNotifications?: boolean;
  analyticsConsent?: boolean | null;

  /* Apply student number and associated metadata */
  applyStudentNumber?: string; // used for applicants
  studentNumberGeneratedByApply?: boolean;

  /* Attributes for keeping track of user platform metadata */
  initiatedByRole?: IUserRole;
  initiatedOnPlatform?: Platform;

  /* Attributes for keeping track of migration status and history */
  migratedToCasShieldFromApply?: boolean;
  userDocumentMigrationPendingTimestamp?: number | null; // unix timestamp of last migration attempt...
  previousUserIds?: string[]; // used to track previous applicant IDs for a user (email change)
  previousUserEmailAddresses?: string[];
}

export interface IMfaStatusResponse {
  mfaEnabled: boolean;
  gracePeriodExpired: boolean;
  skippedToday: boolean;
}

export type IUserLogin = {
  login_time: number;
  refreshToken?: string;
  clientId?: string;
} & {
  [key: string]: string | number | undefined;
};

export interface IUserLogins extends IUser {
  logins?: IUserLogin[];
}
